package fr.frux.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import fr.frux.app.resources.Res
import fr.frux.app.resources.install_banner_action
import fr.frux.app.resources.install_banner_close
import fr.frux.app.resources.install_banner_subtitle
import fr.frux.app.resources.install_banner_title
import fr.frux.core.designsystem.theme.FruxSpacing
import org.jetbrains.compose.resources.stringResource

const val INSTALL_BANNER_DISMISS_DAYS = 7
const val INSTALL_BANNER_STORAGE_KEY = "install_banner_dismissed_until"

private const val MS_PER_DAY = 86_400_000L
private const val PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=fr.frux&referrer="
private const val DEFAULT_UTM_SOURCE = "frux-web"
private val UTM_KEYS = listOf("utm_source", "utm_medium", "utm_campaign")

/** Échéance à écrire en stockage quand la bannière vient d'être refermée à [nowEpochMs]. */
fun installBannerDismissUntil(nowEpochMs: Long): Long = nowEpochMs + INSTALL_BANNER_DISMISS_DAYS * MS_PER_DAY

data class InstallPromptInput(
    val userAgent: String,
    val search: String,
    val referrerHost: String?,
    val ownHost: String,
    val dismissedUntilEpochMs: Long?,
    val nowEpochMs: Long,
)

/**
 * Lien Play à proposer, ou null : pas Android, pas d'arrivée par un lien, ou bannière refermée il
 * y a moins de sept jours. Arrivée par un lien = `utm_source` dans l'URL d'ouverture, ou référent
 * d'un autre site. Le `referrer` Play reporte les trois `utm_*` (source par défaut `frux-web`),
 * pour que la fiche Play garde l'origine du visiteur.
 */
fun installPromptLink(input: InstallPromptInput): String? {
    val params = queryParams(input.search)
    val utmSource = params["utm_source"]?.takeIf { it.isNotEmpty() }
    val externalReferrer = input.referrerHost?.takeIf { it.isNotBlank() && it != input.ownHost }
    val dismissed = input.dismissedUntilEpochMs?.let { input.nowEpochMs < it } == true
    val eligible =
        input.userAgent.contains("Android", ignoreCase = true) &&
            (utmSource != null || externalReferrer != null) &&
            !dismissed
    if (!eligible) return null
    val referrer =
        UTM_KEYS.joinToString("&") { key ->
            val value = if (key == "utm_source") utmSource ?: DEFAULT_UTM_SOURCE else params[key].orEmpty()
            "$key=$value"
        }
    return PLAY_STORE_URL + percentEncode(referrer)
}

private fun queryParams(search: String): Map<String, String> =
    search
        .removePrefix("?")
        .split('&')
        .filter { it.isNotEmpty() }
        .associate { it.substringBefore('=') to it.substringAfter('=', "") }

private fun percentEncode(value: String): String =
    buildString {
        for (byte in value.encodeToByteArray()) {
            val code = byte.toInt() and 0xFF
            val char = code.toChar()
            if (code < 0x80 && (char.isLetterOrDigit() || char in "-._~")) {
                append(char)
            } else {
                append('%').append(code.toString(16).uppercase().padStart(2, '0'))
            }
        }
    }

/** Bandeau de hauteur propre, posé au-dessus du contenu : il ne recouvre pas la carte. */
@Composable
fun InstallBanner(
    prompt: InstallPrompt,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.secondaryContainer,
        contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
    ) {
        Row(
            modifier = Modifier.padding(start = FruxSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(Modifier.weight(1f).padding(vertical = FruxSpacing.sm)) {
                Text(
                    text = stringResource(Res.string.install_banner_title),
                    style = MaterialTheme.typography.titleSmall,
                )
                Text(
                    text = stringResource(Res.string.install_banner_subtitle),
                    style = MaterialTheme.typography.bodySmall,
                )
            }
            TextButton(onClick = { uriHandler.openUri(prompt.playUrl) }) {
                Text(stringResource(Res.string.install_banner_action))
            }
            IconButton(onClick = prompt.dismiss) {
                Icon(
                    imageVector = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.install_banner_close),
                )
            }
        }
    }
}
